// NOTE: Before modifying CDR ingestion, pagination, or grouping, review docs/TELEPHONY_CDR_ARCHITECTURE.md
import { useState } from 'react';
import { FileExplorer } from './FileExplorer';
import { useToast } from '@/components/ui/ToastProvider';
import { CdrViewerProps } from './cdr/cdrTypes';
import { useCdrData } from './cdr/useCdrData';
import { useAudioPlayback } from './cdr/useAudioPlayback';
import { useTranscription } from './cdr/useTranscription';
import { CdrFilterBar } from './cdr/CdrFilterBar';
import { CdrRowItem } from './cdr/CdrRowItem';
import { TranscriptionModal } from './cdr/TranscriptionModal';
import { CdrPaginationControls } from './cdr/CdrPaginationControls';

export function CdrViewer(props: CdrViewerProps) {
    const { serverId, fetchCdrs, audioDownloadUrlPrefix, isLight = false, resolveCnamMap, crm } = props;
    const { showToast } = useToast();
    const [isExplorerOpen, setIsExplorerOpen] = useState(false);

    const {
        cdrsGroups,
        pendingNewCallsCount,
        setPendingNewCallsCount,
        highlightedId,
        cdrsLoading,
        cdrsLastRefreshed,
        cdrsPage,
        cdrsTotal,
        cdrsSearch,
        setCdrsSearch,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        searchArchive,
        setSearchArchive,
        activeFilters,
        setActiveFilters,
        expandedCdrId,
        expandedMissedSegments,
        setExpandedMissedSegments,
        isWsConnected,
        globalExtNames,
        cnamMap,
        loadCdrs,
        toggleCdrDetails,
        formatSeconds,
    } = useCdrData({ serverId, fetchCdrs, audioDownloadUrlPrefix, resolveCnamMap });

    const {
        playingFileKey,
        audioProgress,
        isBuffering,
        audioRef,
        togglePlay,
        handleAudioTimeUpdate,
        handleAudioEnded,
        setIsBuffering,
    } = useAudioPlayback();

    const {
        transcriptionModalOpen,
        setTranscriptionModalOpen,
        transcriptionTargetFile,
        transcriptionStatus,
        transcriptionText,
        transcriptionError,
        transcriptionExistsMap,
        triggerTranscription,
        openTranscriptionModal,
    } = useTranscription({ serverId, audioDownloadUrlPrefix, crm });

    const textColor = isLight ? '#09090b' : 'white';
    const mutedColor = isLight ? '#6b7280' : '#a1a1aa';
    const borderColor = isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.05)';
    const bgExpanded = isLight ? '#f9fafb' : 'rgba(255,255,255,0.02)';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <audio
                ref={audioRef}
                src={playingFileKey || undefined}
                onTimeUpdate={handleAudioTimeUpdate}
                onEnded={handleAudioEnded}
                onPlaying={() => setIsBuffering(false)}
                onWaiting={() => setIsBuffering(true)}
                style={{ display: 'none' }}
            />

            <TranscriptionModal
                isOpen={transcriptionModalOpen}
                targetFile={transcriptionTargetFile}
                onClose={() => setTranscriptionModalOpen(false)}
                transcriptionStatus={transcriptionStatus || 'loading'}
                transcriptionText={transcriptionText}
                transcriptionError={transcriptionError}
                crm={crm}
                triggerTranscription={triggerTranscription}
                showToast={showToast}
                isLight={isLight}
            />

            {isExplorerOpen && (
                <FileExplorer
                    serverId={serverId}
                    isLight={isLight}
                    readOnly={true}
                    baseDirectory="audio/"
                    onTranscribeRequest={(filename, fileKey) => {
                        const sep = audioDownloadUrlPrefix && audioDownloadUrlPrefix.includes('?') ? '&' : '?';
                        const url = audioDownloadUrlPrefix ? `${audioDownloadUrlPrefix}${sep}key=${encodeURIComponent(fileKey)}` : '';
                        const txtUrl = url ? url.replace('.mp3', '.txt').replace('.wav', '.txt') : '';
                        openTranscriptionModal(filename, fileKey, url, txtUrl);
                    }}
                    apiAuthQueryParams={audioDownloadUrlPrefix ? (() => {
                        try {
                            const parsedUrl = new URL(audioDownloadUrlPrefix, window.location.origin);
                            const match = audioDownloadUrlPrefix.match(/\/api\/v1\/crm-dashboard\/([^/]+)/);
                            const slug = match ? match[1] : '';
                            const crmPw = (typeof window !== 'undefined' ? localStorage.getItem(`crm-pw-${slug}`) : '') || parsedUrl.searchParams.get('x-crm-password') || '';
                            const cdrPw = (typeof window !== 'undefined' ? (sessionStorage.getItem(`crm-cdr-pw-${slug}`) || localStorage.getItem(`crm-cdr-pw-${slug}`)) : '') || parsedUrl.searchParams.get('x-crm-cdr-password') || '';
                            const params = new URLSearchParams();
                            if (slug) params.set('x-crm-slug', slug);
                            if (crmPw) params.set('x-crm-password', crmPw);
                            if (cdrPw) params.set('x-crm-cdr-password', cdrPw);
                            const str = params.toString();
                            return str || undefined;
                        } catch(e) {}
                        return undefined;
                    })() : undefined}
                    onClose={() => setIsExplorerOpen(false)}
                />
            )}

            <CdrFilterBar
                cdrsSearch={cdrsSearch}
                setCdrsSearch={setCdrsSearch}
                searchArchive={searchArchive}
                setSearchArchive={setSearchArchive}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                cdrsPage={cdrsPage}
                cdrsTotal={cdrsTotal}
                cdrsLoading={cdrsLoading}
                cdrsLastRefreshed={cdrsLastRefreshed}
                isWsConnected={isWsConnected}
                pendingNewCallsCount={pendingNewCallsCount}
                setPendingNewCallsCount={setPendingNewCallsCount}
                loadCdrs={loadCdrs}
                setIsExplorerOpen={setIsExplorerOpen}
                isLight={isLight}
                cdrsGroupsCount={cdrsGroups.length}
            />

            {cdrsLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: mutedColor }}>Loading Records...</div>
            ) : cdrsGroups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: mutedColor, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                    <div>{searchArchive ? 'No Call Detail Records found in cold archive.' : 'No Call Detail Records found in active index (past 365 days).'}</div>
                    {!searchArchive && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchArchive(true);
                                loadCdrs(0, cdrsSearch, startDate, endDate, activeFilters, false, true);
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                padding: '0.6rem 1.25rem',
                                background: isLight ? '#0284c7' : 'rgba(56, 189, 248, 0.15)',
                                color: isLight ? '#ffffff' : '#38bdf8',
                                border: isLight ? 'none' : '1px solid rgba(56, 189, 248, 0.3)',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 8v13H3V8"></path><path d="M1 3h22v5H1z"></path><path d="M10 12h4"></path></svg>
                            Search Cold Archive (R2 Storage)
                        </button>
                    )}
                </div>
            ) : (
                cdrsGroups.map((rawGroup, i) => (
                    <CdrRowItem
                        key={rawGroup[0]?.id || i}
                        rawGroup={rawGroup}
                        globalExtNames={globalExtNames}
                        cnamMap={cnamMap}
                        crm={crm}
                        expandedCdrId={expandedCdrId}
                        toggleCdrDetails={toggleCdrDetails}
                        expandedMissedSegments={expandedMissedSegments}
                        setExpandedMissedSegments={setExpandedMissedSegments}
                        transcriptionExistsMap={transcriptionExistsMap}
                        serverId={serverId}
                        audioDownloadUrlPrefix={audioDownloadUrlPrefix}
                        highlightedId={highlightedId}
                        playingFileKey={playingFileKey}
                        isBuffering={isBuffering}
                        audioProgress={audioProgress}
                        togglePlay={togglePlay}
                        openTranscriptionModal={openTranscriptionModal}
                        formatSeconds={formatSeconds}
                        isLight={isLight}
                        textColor={textColor}
                        mutedColor={mutedColor}
                        borderColor={borderColor}
                        bgExpanded={bgExpanded}
                    />
                ))
            )}

            {cdrsGroups.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${borderColor}` }}>
                    <CdrPaginationControls
                        cdrsPage={cdrsPage}
                        cdrsTotal={cdrsTotal}
                        cdrsGroupsCount={cdrsGroups.length}
                        loadCdrs={loadCdrs}
                        isLight={isLight}
                        isBottom={true}
                        searchArchive={searchArchive}
                    />
                </div>
            )}
        </div>
    );
}
